import React, { useMemo } from "react";
import { FileText } from "lucide-react";
import type { MaterialDto, OptionDto } from "../../../../domain/types/company/company-product-system";
import { companyProductSystemUseCase } from "../../../../di/container";
import { OptionCard } from "./OptionCard";
import { ReorderError, SortableItem, SortableList, useInstantReorder } from "./sortable";

/**
 * 옵션이 아닌 주문서 섹션의 자리 카드. 내용은 다른 곳(용지 설정·인쇄 구성)에서 관리하고 여기서는 위치만 정한다.
 * 책자의 표지·내지는 책자 상품에서만 자리 카드다. 책자가 아닌 상품에 남은 그 옵션은 다른 곳에서 볼 방법이 없어 일반 카드로 보여 준다.
 */
const SECTION_PLACEHOLDERS: Record<string, { title: string; description: string; bookOnly: boolean }> = {
  BOOK_COVER_PAPER: { title: "표지 사양", description: "표지 용지는 상품 용지 설정에서 관리합니다. 쇼핑몰에서 표지 사양이 표시될 위치만 정합니다.", bookOnly: true },
  BOOK_INNER_PAPER: { title: "내지 사양", description: "내지 용지는 상품 용지 설정에서 관리합니다. 쇼핑몰에서 내지 사양이 표시될 위치만 정합니다.", bookOnly: true },
  PAPER: { title: "용지 선택", description: "용지는 상품 용지 설정에서 관리합니다. 쇼핑몰에서 용지 선택이 표시될 위치만 정합니다.", bookOnly: false },
  PRINT: { title: "인쇄", description: "인쇄 도수·단면/양면은 인쇄 구성에서 관리합니다. 쇼핑몰에서 인쇄 선택이 표시될 위치만 정합니다.", bookOnly: false },
};

const sortByDisplayOrder = (options: OptionDto[]) =>
  [...options].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0) || a.id - b.id);

/**
 * 조건부 옵션이 자기 조건 값을 가진 옵션보다 앞에 있으면 경고 문구.
 * 쇼핑몰에서는 동작하지만, 고객이 아래 옵션을 고른 뒤 위로 되돌아가야 새 옵션이 보인다.
 */
export const findTriggerOrderWarnings = (orderedOptions: OptionDto[]): Map<number, string> => {
  const ownerByValueId = new Map<number, number>();
  orderedOptions.forEach((option) =>
    (option.attributes || []).forEach((attribute) =>
      (attribute.values || []).forEach((value) => ownerByValueId.set(value.id, option.id)),
    ),
  );
  const indexById = new Map(orderedOptions.map((option, index) => [option.id, index]));
  const warnings = new Map<number, string>();
  orderedOptions.forEach((option, index) => {
    const ownerIds = (option.triggerAttributeValueIds || [])
      .map((valueId) => ownerByValueId.get(valueId))
      .filter((ownerId): ownerId is number => ownerId !== undefined && ownerId !== option.id);
    const laterOwners = ownerIds.filter((ownerId) => (indexById.get(ownerId) ?? -1) > index);
    if (laterOwners.length > 0) {
      const names = Array.from(new Set(laterOwners))
        .map((ownerId) => orderedOptions[indexById.get(ownerId)!]?.name)
        .filter(Boolean)
        .join(", ");
      warnings.set(option.id, `조건 옵션(${names})보다 앞에 있어 고객이 위로 되돌아가야 이 옵션이 보입니다.`);
    }
  });
  return warnings;
};

interface Props {
  companyId: number;
  productId: number;
  options: OptionDto[];
  isBookProduct: boolean;
  materials: MaterialDto[];
  onDeleteOption: (optionId: number) => void;
  onRefresh: () => Promise<void>;
}

export const ProductOptionList: React.FC<Props> = ({
  companyId, productId, options, isBookProduct, materials, onDeleteOption, onRefresh,
}) => {
  const sorted = useMemo(() => sortByDisplayOrder(options), [options]);
  // 서버는 형제 전체를 받아야 순서를 저장한다. 숨겨진 옵션도 빠짐없이 넣는다
  const { ids, move, error, clearError } = useInstantReorder(
    sorted.map((option) => option.id),
    async (orderedIds) => {
      await companyProductSystemUseCase.reorderOptionItems(companyId, productId, { level: "option" }, orderedIds);
      // 가격 미리보기의 용지·인쇄 순서 등 화면의 다른 곳도 새 순서를 따르도록 다시 불러온다
      void onRefresh().catch(() => {});
    },
  );
  const byId = useMemo(() => new Map(options.map((option) => [option.id, option])), [options]);
  const ordered = useMemo(
    () => ids.map((id) => byId.get(id)).filter((option): option is OptionDto => !!option),
    [ids, byId],
  );
  const warnings = useMemo(() => findTriggerOrderWarnings(ordered), [ordered]);

  const placeholderOf = (option: OptionDto) => {
    const placeholder = SECTION_PLACEHOLDERS[option.optionType || ""];
    return placeholder && (!placeholder.bookOnly || isBookProduct) ? placeholder : null;
  };
  const isPaperSection = (option: OptionDto) => !!placeholderOf(option);
  const visibleCount = ordered.filter((option) => !isPaperSection(option)).length;

  if (visibleCount === 0 && !ordered.some(isPaperSection)) {
    return <p className="text-[14px] text-[#bbb] py-8 text-center">옵션이 없습니다</p>;
  }

  return (
    <div>
      <ReorderError message={error} onClose={clearError} />
      <SortableList scope="option" ids={ids} onMove={move}>
        <div className="space-y-6">
          {ordered.map((option) => (
            <SortableItem key={option.id} scope="option" id={option.id} label={`'${placeholderOf(option)?.title || option.name}' 순서 이동`}>
              {({ setNodeRef, style, handle }) => (
                <div ref={setNodeRef} style={style}>
                  {isPaperSection(option) ? (
                    <div className="border border-dashed border-[#E0E0E0] rounded-[12px] bg-[#FAFAFA] px-5 py-4 flex items-center gap-3 text-left">
                      {handle}
                      <FileText className="w-[18px] h-[18px] text-[#888]" strokeWidth={1.8} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[15px] font-bold text-[#222]">{placeholderOf(option)!.title}</div>
                        <div className="text-[12px] text-[#888] mt-0.5">{placeholderOf(option)!.description}</div>
                      </div>
                    </div>
                  ) : (
                    <OptionCard
                      companyId={companyId}
                      productId={productId}
                      option={option}
                      allOptions={options}
                      materials={materials}
                      onDeleteOption={onDeleteOption}
                      onRefresh={onRefresh}
                      dragHandle={handle}
                      orderWarning={warnings.get(option.id)}
                    />
                  )}
                </div>
              )}
            </SortableItem>
          ))}
        </div>
      </SortableList>
    </div>
  );
};
